<template>
  <div class="mb-4">
    <!-- Photos Section -->
    <div class="mb-4" v-if="images && images.length > 0">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="text-uppercase font-weight-bold text-secondary mb-0">
          <i class="fas fa-images mr-1"></i>
          Photos
          <b-badge variant="secondary" pill class="ml-1">
            {{ selectedImageIds.length }} of {{ images.length }} selected
          </b-badge>
        </h6>
        <div>
          <b-link class="small mr-2" @click="selectAllImages">Select All</b-link>
          <b-link class="small" @click="deselectAllImages">Deselect All</b-link>
        </div>
      </div>
      <div class="row">
        <div class="col-4 col-md-3 col-lg-2 mb-3" v-for="image in images" :key="image.id">
          <div class="media-thumb-cell position-relative cursor-pointer"
               :class="isImageSelected(image.id) ? 'selected' : ''"
               @click="toggleImage(image.id)">
            <img
              :src="image.thumbnailMicroPublicUrl || image.thumbnailPublicUrl || image.publicUrl"
              class="media-thumb-img"
              :alt="image.fileName || image.name || ''"
              @error="$event.target.src = image.thumbnailPublicUrl || image.publicUrl || ''"
            />
            <div class="selection-overlay d-flex align-items-center justify-content-center"
                 :class="isImageSelected(image.id) ? 'selected-overlay' : 'unselected-overlay'">
              <i v-if="isImageSelected(image.id)" class="fas fa-check-circle fa-2x text-white"></i>
            </div>
            <div v-if="isImageSelected(image.id)" class="selected-badge">
              <i class="fas fa-check-circle text-white"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="images" class="text-muted text-center py-3 mb-4 border rounded">
      <i class="fas fa-photo-video fa-2x mb-2 d-block text-muted"></i>
      No photos attached to this incident.
    </div>

    <!-- Videos Section -->
    <div v-if="mediaLoading" class="mb-4 text-center py-3 border rounded text-muted">
      <b-spinner small class="mr-1"></b-spinner> Loading videos…
    </div>
    <div class="mb-4" v-else-if="videos && videos.length > 0">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="text-uppercase font-weight-bold text-secondary mb-0">
          <i class="fas fa-video mr-1"></i>
          Videos
          <b-badge variant="secondary" pill class="ml-1">
            {{ selectedVideoIds.length }} of {{ videos.length }} selected
          </b-badge>
        </h6>
        <div>
          <b-link class="small mr-2" @click="selectAllVideos">Select All</b-link>
          <b-link class="small" @click="deselectAllVideos">Deselect All</b-link>
        </div>
      </div>
      <div class="row">
        <div class="col-4 col-md-3 col-lg-2 mb-3" v-for="video in videos" :key="video.id">
          <div class="media-thumb-cell position-relative cursor-pointer"
               :class="isVideoSelected(video.id) ? 'selected' : ''"
               @click="toggleVideo(video.id)">
            <FileThumbnail
              :thumbnail-url="video.thumbnailPublicUrl"
              :full-url="video.publicUrl"
              file-type="video"
              :processing-status="video.videoProcessingStatus"
              img-class="media-thumb-img"
            />
            <div class="selection-overlay d-flex align-items-center justify-content-center"
                 :class="isVideoSelected(video.id) ? 'selected-overlay' : 'unselected-overlay'">
              <i v-if="isVideoSelected(video.id)" class="fas fa-check-circle fa-2x text-white"></i>
            </div>
          </div>
          <small class="text-truncate d-block mt-1 text-center" :title="video.name || video.fileName">
            {{ video.name || video.fileName }}
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FileThumbnail from '@/components/FileThumbnail.vue'

export default {
  name: 'IncidentReportMediaSelector',
  components: { FileThumbnail },
  props: {
    images: {
      type: Array,
      default: () => []
    },
    videos: {
      type: Array,
      default: () => []
    },
    selectedImageIds: {
      type: Array,
      default: () => []
    },
    selectedVideoIds: {
      type: Array,
      default: () => []
    },
    mediaLoading: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    isImageSelected (id) {
      return this.selectedImageIds.includes(id)
    },
    isVideoSelected (id) {
      return this.selectedVideoIds.includes(id)
    },
    toggleImage (id) {
      const current = [...this.selectedImageIds]
      const idx = current.indexOf(id)
      if (idx >= 0) current.splice(idx, 1)
      else current.push(id)
      this.$emit('update:selectedImageIds', current)
    },
    toggleVideo (id) {
      const current = [...this.selectedVideoIds]
      const idx = current.indexOf(id)
      if (idx >= 0) current.splice(idx, 1)
      else current.push(id)
      this.$emit('update:selectedVideoIds', current)
    },
    selectAllImages () {
      this.$emit('update:selectedImageIds', this.images.map(i => i.id))
    },
    deselectAllImages () {
      this.$emit('update:selectedImageIds', [])
    },
    selectAllVideos () {
      this.$emit('update:selectedVideoIds', this.videos.map(v => v.id))
    },
    deselectAllVideos () {
      this.$emit('update:selectedVideoIds', [])
    }
  }
}
</script>

<style scoped>
.media-thumb-cell {
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 6px;
  border: 2px solid transparent;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.media-thumb-cell.selected {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.3);
}
.media-thumb-cell:hover {
  border-color: #adb5bd;
}
/* Direct img (photos) — fills the square cell */
.media-thumb-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
/* For video thumbnails rendered via FileThumbnail, make its root wrapper fill the cell */
.media-thumb-cell ::v-deep .file-thumbnail-wrapper {
  height: 100%;
}
.selection-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 4px;
  transition: background-color 0.15s;
}
.selected-overlay {
  background: rgba(0, 123, 255, 0.3);
}
.unselected-overlay {
  background: transparent;
}
.media-thumb-cell:hover .unselected-overlay {
  background: rgba(0, 0, 0, 0.1);
}
.cursor-pointer {
  cursor: pointer;
}
</style>
